// The recent-session trail the feedback button attaches: pages opened, what was
// clicked, which fields changed (never their values), API calls and page
// errors. Kept in sessionStorage so a reload, which is often how a bug ends,
// doesn't wipe the steps that led to it. Nothing leaves the browser except
// through the text Gabriel copies himself.
import { cleanUrl } from "./prompt.mjs";

const KEY = "crm_feedback_trail";
const KEEP_MS = 45 * 60 * 1000;
const MAX = { actions: 40, requests: 30, errors: 10 };

let state = load();

function load() {
  try {
    const s = JSON.parse(sessionStorage.getItem(KEY) || "null");
    if (s && s.actions && s.requests && s.errors) return s;
  } catch {
    /* fresh trail */
  }
  return { actions: [], requests: [], errors: [] };
}

let saveTimer = null;
function save() {
  if (saveTimer) return;
  saveTimer = setTimeout(() => {
    saveTimer = null;
    try {
      sessionStorage.setItem(KEY, JSON.stringify(state));
    } catch {
      /* storage full or blocked: the trail just won't survive a reload */
    }
  }, 300);
}

function push(list, item) {
  const arr = state[list];
  arr.push({ ...item, at: Date.now() });
  if (arr.length > MAX[list]) arr.splice(0, arr.length - MAX[list]);
  save();
}

export function recordRoute(path) {
  const last = [...state.actions].reverse().find((a) => a.kind === "route");
  if (last && last.text === path) return;
  push("actions", { kind: "route", text: path });
}

export function snapshotTrail() {
  const since = Date.now() - KEEP_MS;
  const keep = (arr) => arr.filter((x) => x.at >= since);
  return { actions: keep(state.actions), requests: keep(state.requests), errors: keep(state.errors) };
}

const squash = (s, n = 60) => {
  const t = String(s || "").replace(/\s+/g, " ").trim();
  return t.length > n ? `${t.slice(0, n - 1)}…` : t;
};

const CLICKABLE = 'button, a, [role="button"], [role="tab"], [role="menuitem"], [role="option"], [role="row"], tr, summary, label, select, input, textarea';

function kindOf(el) {
  const tag = el.tagName.toLowerCase();
  const role = el.getAttribute("role");
  if (role === "tab") return "tab";
  if (role === "menuitem" || role === "option") return "menu item";
  if (role === "row" || tag === "tr") return "row";
  if (tag === "a") return "link";
  if (tag === "input" && (el.type === "checkbox" || el.type === "radio")) return el.type;
  if (tag === "input" || tag === "textarea" || tag === "select") return "field";
  return "button";
}

// A field's human name: its label, aria-label, placeholder or name, never its value.
function fieldName(el) {
  const label = el.id && document.querySelector(`label[for="${CSS.escape(el.id)}"]`);
  return squash(
    el.getAttribute("aria-label") ||
      (label && label.textContent) ||
      (el.closest("label") && el.closest("label").textContent) ||
      el.getAttribute("placeholder") ||
      el.getAttribute("name") ||
      el.tagName.toLowerCase(),
    40,
  );
}

function describe(target) {
  const el = target.closest && target.closest(CLICKABLE);
  if (!el) {
    // A bare click on text or a card: name it by the text under the pointer.
    const text = squash(target.textContent, 40);
    return text ? `on "${text}"` : null;
  }
  const kind = kindOf(el);
  if (kind === "field") return `into field "${fieldName(el)}"`;
  if (kind === "row") {
    // A table row's text is every cell run together; its first two cells say which row.
    const cells = [...el.querySelectorAll("td, [role='cell'], [role='gridcell']")]
      .map((c) => squash(c.innerText || c.textContent, 40))
      .filter((t) => t && t !== "—");
    if (cells.length) return `row "${cells.slice(0, 2).join(" · ")}"`;
  }
  const name =
    el.getAttribute("aria-label") ||
    el.getAttribute("title") ||
    (kind === "checkbox" || kind === "radio" ? fieldName(el) : el.innerText || el.textContent);
  const where = el.closest('[class^="rec-"], [class*=" rec-"]') ? " (in the record)" : "";
  return `${kind} "${squash(name) || "(icon)"}"${where}`;
}

let installed = false;
export function installTrail() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  document.addEventListener(
    "click",
    (e) => {
      if (!(e.target instanceof Element) || e.target.closest(".fb-root")) return;
      const text = describe(e.target);
      if (text) push("actions", { kind: "click", text });
    },
    true,
  );

  // Which field changed, not what was typed into it.
  document.addEventListener(
    "change",
    (e) => {
      const el = e.target;
      if (!(el instanceof Element) || el.closest(".fb-root")) return;
      if (el.matches("select")) {
        const opt = el.selectedOptions && el.selectedOptions[0];
        push("actions", { kind: "edit", text: `"${fieldName(el)}" to "${squash(opt && opt.textContent, 40)}"` });
      } else if (el.matches("input, textarea") && el.type !== "checkbox" && el.type !== "radio") {
        if (el.type === "password") return;
        push("actions", { kind: "edit", text: `field "${fieldName(el)}"` });
      }
    },
    true,
  );

  document.addEventListener(
    "keydown",
    (e) => {
      if (e.key !== "Escape" && !(e.key === "Enter" && (e.ctrlKey || e.metaKey))) return;
      if (e.target instanceof Element && e.target.closest(".fb-root")) return;
      push("actions", { kind: "key", text: e.key === "Escape" ? "Esc" : "Ctrl+Enter" });
    },
    true,
  );

  window.addEventListener("error", (e) => {
    push("errors", { text: squash(`${e.message || "Error"}${e.filename ? ` (${e.filename.split("/").pop()}:${e.lineno})` : ""}`, 300) });
  });
  window.addEventListener("unhandledrejection", (e) => {
    const r = e.reason;
    push("errors", { text: squash((r && r.message) || r || "Unhandled rejection", 300) });
  });
  const origError = console.error;
  console.error = (...args) => {
    try {
      push("errors", { text: squash(args.map((a) => (a instanceof Error ? a.message : typeof a === "string" ? a : safeJson(a))).join(" "), 300) });
    } catch {
      /* never break logging */
    }
    origError.apply(console, args);
  };

  // Every /api call, with its status and time. The body is never read here: a
  // failed call's error message comes from a clone so the caller still gets it.
  const origFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const url = typeof input === "string" ? input : input && input.url;
    if (!url || !/\/api\//.test(url) || /\/api\/sse/.test(url)) return origFetch(input, init);
    const method = String(init.method || (input && input.method) || "GET").toUpperCase();
    const path = cleanUrl(url);
    const t0 = performance.now();
    try {
      const res = await origFetch(input, init);
      const entry = { method, path, status: res.status, ms: Math.round(performance.now() - t0) };
      if (!res.ok) {
        res
          .clone()
          .json()
          .then((b) => b && b.error && (entry.error = squash(b.error, 200)))
          .catch(() => {})
          .finally(() => push("requests", entry));
      } else {
        push("requests", entry);
      }
      return res;
    } catch (err) {
      push("requests", { method, path, status: 0, ms: Math.round(performance.now() - t0), error: squash(err && err.message, 120) });
      throw err;
    }
  };
}

function safeJson(v) {
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}
